import { forwardRef } from "react";
import { cn } from "./cn.ts";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg" | "icon";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const variants: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-accent-fg hover:opacity-90 active:opacity-80 shadow-sm",
  secondary:
    "bg-surface-2 text-fg hover:bg-border/60 border border-border",
  ghost: "text-fg-muted hover:bg-surface-2 hover:text-fg",
  danger: "bg-danger text-white hover:opacity-90",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-sm gap-1.5",
  md: "h-10 px-4 text-sm gap-2",
  lg: "h-12 px-6 text-base gap-2",
  icon: "size-10 p-0",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({ className, variant = "primary", size = "md", type, ...rest }, ref) {
    return (
      <button
        ref={ref}
        // Buttons inside forms default to submit, which reloads the page in a
        // client-only app. Every button here is an action unless told otherwise.
        type={type ?? "button"}
        className={cn(
          "inline-flex items-center justify-center rounded-lg font-medium",
          "transition-[opacity,background-color,transform] duration-150",
          "disabled:pointer-events-none disabled:opacity-50",
          "whitespace-nowrap select-none",
          variants[variant],
          sizes[size],
          className,
        )}
        {...rest}
      />
    );
  },
);
